<template>
  <AISuggestedContactsBasePanel
    :contact-list="contactList"
    :has-results="showResults"
    :media-release="mediaRelease"
    :selected-ids="selectedIds"
    :updating="updating"
    @add="handleAddContacts"
    @close="emit('close')"
  >
    <AISuggestedContactsListResponse
      v-if="contactList || mediaRelease"
      v-model:contact-type="contactOptions.contactType"
      v-model:selected-ids="selectedIds"
      v-model:sort-direction="contactOptions.sortDirection"
      v-model:sort-field="contactOptions.sortField"
      :chat-delay="500"
      :chat-state="chatState"
      class="tw:px-4"
      :contact-list="contactList"
      :contacts="contacts"
      :loading="loadingContacts"
      :media-release="mediaRelease"
      @chat-type-complete="handleTypeComplete"
    />
  </AISuggestedContactsBasePanel>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, reactive, ref, watch } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import AISuggestedContactsBasePanel from "shared/components/AI/AISuggestedContacts/AISuggestedContactsBasePanel.vue";
import AISuggestedContactsListResponse, {
  type AISuggestedContactsListResponseProps,
} from "shared/components/AI/AISuggestedContacts/AISuggestedContactsListResponse.vue";
import useSuggestedContacts, {
  type SuggestedContactOptions,
} from "shared/composables/useSuggestedContacts";
import useSuggestedContactsSuggestions from "shared/composables/useSuggestedContactsSuggestions";
import useAIStore from "shared/stores/ai";

const emit = defineEmits(["close", "selected-ids"]);

const aiStore = useAIStore();

const { aiSuggestedContactsFilter } = storeToRefs(aiStore);

const contactOptions = reactive<SuggestedContactOptions>({
  contactType: "influencer",
  sortDirection: "desc",
  sortField: "relevance",
});

const { loadingContacts, contacts, loadContacts } =
  useSuggestedContacts(contactOptions);

const { contactList, mediaRelease, selectedIds, updating, add, init } =
  useSuggestedContactsSuggestions();

const chatState =
  ref<AISuggestedContactsListResponseProps["chatState"]>("moment");

const showResults = ref<boolean>(false);

const typeLabel = computed<string>(() => {
  if (mediaRelease.value) {
    return getLocaleText(
      `ai_suggested_contacts_panel.${mediaRelease.value.mediaReleaseType}`
    );
  }

  return "";
});

async function handleTypeComplete() {
  if (chatState.value === "moment") {
    await loadContacts(aiSuggestedContactsFilter.value);
    chatState.value = "result";
  } else if (chatState.value === "result") {
    showResults.value = true;
  }
}

async function handleAddContacts() {
  await add(typeLabel.value);
  loadContacts(aiSuggestedContactsFilter.value);
}

watch(aiSuggestedContactsFilter, init, { immediate: true });

watch(
  () => contactOptions.contactType,
  () => {
    loadContacts(aiSuggestedContactsFilter.value);
  }
);
</script>

<style scoped lang="scss">
@import "shared/css/components/ai.scss";
</style>
